/**
 * Auto-Organize Modal — shows the result of the auto-structurer and lets the
 * admin teach the platform any unknown labels (Unknown Fields Suggester).
 *
 * Flow:
 *   1. Admin clicks "Auto-Organize" in the template editor.
 *   2. Parent posts the current body to /auto-structure.
 *   3. This modal displays:
 *      - Replacements (green, already auto-mapped)
 *      - Warnings (amber, unmapped labels) each with a token dropdown
 *   4. For each warning, admin picks the correct canonical token from the
 *      dropdown (grouped) + scope (A/B/neutral) and clicks "Teach".
 *   5. Modal re-runs auto-structure and shows the updated preview.
 *   6. Admin clicks "Apply to editor" to push the normalized body back.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  X, Wand2, CheckCircle2, AlertTriangle, GraduationCap, Save, Loader2, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/cms/document-templates`;

const SCOPE_OPTIONS = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'a', label: 'Party A (Issuer)' },
  { value: 'b', label: 'Party B (Counterparty)' },
];

const AutoOrganizeModal = ({ open, onClose, initialResult, bodyOriginal, language, onApply }) => {
  const { toast } = useToast();
  const [result, setResult] = useState(initialResult || null);
  const [canonicalTokens, setCanonicalTokens] = useState([]);
  const [picks, setPicks] = useState({}); // { [labelNormalized]: { token, scope } }
  const [teaching, setTeaching] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => { setResult(initialResult || null); }, [initialResult]);

  // Load canonical tokens once
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('admin_token');
    axios.get(`${ENDPOINT}/canonical-tokens`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCanonicalTokens(r.data || []))
      .catch(() => setCanonicalTokens([]));
  }, [open]);

  const tokensGrouped = useMemo(() => {
    const g = {};
    for (const t of canonicalTokens) {
      if (!g[t.group]) g[t.group] = [];
      g[t.group].push(t);
    }
    return g;
  }, [canonicalTokens]);

  const warnings = result?.warnings || [];
  const replacements = result?.replacements || [];

  const handlePickChange = (labelKey, field, value) => {
    setPicks((p) => ({
      ...p,
      [labelKey]: { ...(p[labelKey] || { token: '', scope: 'neutral' }), [field]: value },
    }));
  };

  const teachOne = async (warning) => {
    const key = warning.label;
    const pick = picks[key] || {};
    if (!pick.token) {
      toast({ title: 'Select a token first', variant: 'destructive' });
      return;
    }
    setTeaching(true);
    try {
      const t = localStorage.getItem('admin_token');
      await axios.post(`${ENDPOINT}/learned-mappings`, {
        label: warning.label,
        token: pick.token,
        scope_hint: pick.scope || 'neutral',
        language: language || 'auto',
      }, { headers: { Authorization: `Bearer ${t}` } });

      // Re-run auto-structure so the modal preview reflects the new teaching
      const r = await axios.post(`${ENDPOINT}/auto-structure`, {
        body: bodyOriginal,
        language: language || 'pt',
      }, { headers: { Authorization: `Bearer ${t}` } });
      setResult(r.data);
      setPicks((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
      toast({
        title: 'Mapping saved',
        description: `"${warning.label}" → {${pick.token}}. The platform just got smarter.`,
      });
    } catch (err) {
      toast({
        title: 'Teach failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setTeaching(false);
    }
  };

  const handleApply = () => {
    if (!result?.normalized_body) return;
    setApplying(true);
    try {
      onApply(result.normalized_body);
      toast({
        title: 'Applied',
        description: `${replacements.length} fields normalized, ${warnings.length} still unmapped.`,
      });
      onClose();
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="auto-organize-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b bg-gradient-to-r from-[#FF6B35]/5 to-[#F97316]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#F97316] flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#2B2D42]">Auto-Organize Template</h3>
              <p className="text-xs text-[#6C757D]">
                Teach unmapped labels — the platform learns and remembers for all future templates.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="auto-organize-close-btn"
          >
            <X className="w-4 h-4 text-[#6C757D]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="auto-organize-stat-replaced">
              <div className="flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wide">Normalized</span>
              </div>
              <div className="text-2xl font-bold text-emerald-800 mt-1">{replacements.length}</div>
              <div className="text-[11px] text-emerald-700">fields → canonical tokens</div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="auto-organize-stat-warnings">
              <div className="flex items-center gap-1.5 text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wide">Unmapped</span>
              </div>
              <div className="text-2xl font-bold text-amber-800 mt-1">{warnings.length}</div>
              <div className="text-[11px] text-amber-700">labels awaiting teaching</div>
            </div>
            <div className="rounded-lg border border-[#023E8A]/20 bg-[#023E8A]/5 p-3" data-testid="auto-organize-stat-placeholders">
              <div className="flex items-center gap-1.5 text-[#023E8A]">
                <GraduationCap className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wide">Placeholders</span>
              </div>
              <div className="text-2xl font-bold text-[#023E8A] mt-1">{result?.placeholders?.length || 0}</div>
              <div className="text-[11px] text-[#023E8A]">ready to render</div>
            </div>
          </div>

          {/* Warnings — Unknown Fields Suggester */}
          {warnings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h4 className="text-sm font-bold text-[#2B2D42]">Unknown Fields — Teach the platform</h4>
              </div>
              <div className="border border-amber-200 rounded-lg bg-amber-50/30 divide-y divide-amber-100">
                {warnings.map((w, i) => {
                  const key = w.label;
                  const pick = picks[key] || { token: '', scope: 'neutral' };
                  return (
                    <div key={`${key}-${i}`} className="p-3 flex items-center gap-2 flex-wrap" data-testid={`auto-organize-warning-row-${i}`}>
                      <div className="flex-1 min-w-[180px]">
                        <div className="text-sm font-semibold text-[#2B2D42]">{w.label}</div>
                        <div className="text-[11px] text-[#6C757D] font-mono truncate">L{w.line_no}: {w.original}</div>
                      </div>
                      <select
                        className="px-2 py-1.5 border rounded text-xs min-w-[200px]"
                        value={pick.token}
                        onChange={(e) => handlePickChange(key, 'token', e.target.value)}
                        data-testid={`auto-organize-token-select-${i}`}
                      >
                        <option value="">— Pick canonical token —</option>
                        {Object.entries(tokensGrouped).map(([group, items]) => (
                          <optgroup key={group} label={group}>
                            {items.map((t) => (
                              <option key={t.token} value={t.token}>{t.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <select
                        className="px-2 py-1.5 border rounded text-xs"
                        value={pick.scope}
                        onChange={(e) => handlePickChange(key, 'scope', e.target.value)}
                        data-testid={`auto-organize-scope-select-${i}`}
                      >
                        {SCOPE_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={() => teachOne(w)}
                        disabled={teaching || !pick.token}
                        className="bg-[#FF6B35] hover:bg-[#E55A28] text-white text-xs h-8"
                        data-testid={`auto-organize-teach-btn-${i}`}
                      >
                        {teaching ? <Loader2 className="w-3 h-3 animate-spin" /> : <><GraduationCap className="w-3 h-3 mr-1" />Teach</>}
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#6C757D] mt-1">
                Each mapping is remembered — the engine auto-applies it on every future template.
              </p>
            </div>
          )}

          {/* Replacements */}
          {replacements.length > 0 && (
            <details className="border rounded-lg" data-testid="auto-organize-replacements-details">
              <summary className="p-3 cursor-pointer text-sm font-semibold text-[#2B2D42] flex items-center gap-2 hover:bg-slate-50">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                {replacements.length} fields already normalized
              </summary>
              <div className="border-t divide-y divide-slate-100">
                {replacements.map((r, i) => (
                  <div key={i} className="p-2 px-3 flex items-center gap-2 text-xs" data-testid={`auto-organize-replacement-row-${i}`}>
                    <span className="font-medium text-[#2B2D42] flex-1">{r.label}</span>
                    <code className="font-mono text-[#023E8A] bg-[#023E8A]/5 px-1.5 py-0.5 rounded">{`{${r.token}}`}</code>
                    <Badge variant="outline" className="text-[10px] h-5">
                      {r.scope === 'a' ? 'A' : r.scope === 'b' ? 'B' : 'neutral'}
                    </Badge>
                    {r.learned && <Badge className="bg-[#FF6B35] text-white text-[10px] h-5">learned</Badge>}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Preview */}
          {result?.normalized_body && (
            <div>
              <h4 className="text-sm font-bold text-[#2B2D42] mb-2">Normalized Body Preview</h4>
              <pre
                className="whitespace-pre-wrap text-xs font-mono bg-slate-50 border rounded-lg p-3 max-h-64 overflow-y-auto"
                data-testid="auto-organize-preview"
              >
                {result.normalized_body}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t bg-slate-50">
          <div className="text-xs text-[#6C757D]">
            {warnings.length > 0
              ? `${warnings.length} unmapped labels — teach them above, or apply as-is (blanks remain).`
              : 'All labels mapped. Ready to apply.'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="auto-organize-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={applying || !result?.normalized_body}
              className="bg-[#023E8A] hover:bg-[#023E8A]/90 text-white"
              data-testid="auto-organize-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Apply to editor
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Learned Mappings Management panel — compact list for admin to review
 * and remove taught mappings.
 */
export const LearnedMappingsList = ({ onChange }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('admin_token');
      const r = await axios.get(`${ENDPOINT}/learned-mappings`, { headers: { Authorization: `Bearer ${t}` } });
      setRows(r.data || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Forget this learned mapping?')) return;
    try {
      const t = localStorage.getItem('admin_token');
      await axios.delete(`${ENDPOINT}/learned-mappings/${id}`, { headers: { Authorization: `Bearer ${t}` } });
      toast({ title: 'Mapping removed' });
      load();
      onChange?.();
    } catch (err) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  if (loading) return <div className="text-xs text-[#6C757D] p-2"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading…</div>;
  if (!rows.length) return null;

  return (
    <div className="border rounded-lg bg-white" data-testid="learned-mappings-list">
      <div className="p-3 border-b flex items-center gap-2">
        <GraduationCap className="w-4 h-4 text-[#FF6B35]" />
        <h4 className="text-sm font-bold text-[#2B2D42]">Learned Mappings ({rows.length})</h4>
      </div>
      <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
        {rows.map((m) => (
          <div key={m.id} className="p-2 px-3 flex items-center gap-2 text-xs" data-testid={`learned-mapping-row-${m.id}`}>
            <span className="font-semibold text-[#2B2D42] flex-1 truncate">{m.label_display}</span>
            <code className="font-mono text-[#023E8A] bg-[#023E8A]/5 px-1.5 py-0.5 rounded">{`{${m.token}}`}</code>
            <Badge variant="outline" className="text-[10px] h-5">
              {m.scope_hint === 'a' ? 'A' : m.scope_hint === 'b' ? 'B' : 'neutral'}
            </Badge>
            <span className="text-[10px] text-[#6C757D]">×{m.usage_count}</span>
            <button
              onClick={() => handleDelete(m.id)}
              className="p-1 hover:bg-red-50 text-red-500 rounded"
              data-testid={`learned-mapping-delete-${m.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AutoOrganizeModal;
